import { lazy, Suspense, useState, useCallback, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useFeatureIsOn, useGrowthBook } from "@growthbook/growthbook-react";
import { useLocation } from "react-router";
import { AppState } from "@Store/index";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";
import {
  setOpenAmberCopilot,
  setIsChatbotOpen,
} from "@Pages/PropertyPage/components/ZendeskChat/ChatBotReducer";
import wrapperHOC from "@Utils/wrapperHOC";
import { initializeStorage } from "./utils/chatbotStorage";

const Chatbot = lazy(() => import("@Components/Chatbot"));
const ChatbotButton = lazy(() => import("@Components/Chatbot/ChatbotButton"));

const AmberCopilotWrapper = () => {
  const [showButton, setShowButton] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const dispatch = useDispatch();
  const growthbook = useGrowthBook();
  const location = useLocation();
  const prevPathnameRef = useRef<string | null>(null);

  const { isChatbotOpen, leadFormPage, openLeadFormModal, openingSource } = useSelector(
    ({ chatBotReducer, leadFormModal }: AppState) => ({
      isChatbotOpen: chatBotReducer.isChatbotOpen,
      leadFormPage: leadFormModal.leadFormPage,
      openLeadFormModal: leadFormModal.openLeadFormModal,
      openingSource: leadFormModal.openingSource,
    }),
  );

  const isAmberCopilotEnabled = useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.AMBER_COPILOT);
  const isSuccessPage =
    openLeadFormModal &&
    ((leadFormPage === 2 && openingSource !== "virtual_tour_enquiry") ||
      (leadFormPage === 3 && openingSource === "virtual_tour_enquiry"));

  const isPaymentPage =
    typeof window !== "undefined" && window.location.pathname.includes("/payments");

  const isExcludedPage =
    typeof window !== "undefined" &&
    (window.location.pathname.includes("/scholarship") ||
      window.location.pathname.includes("/exams"));

  useEffect(() => {
    const initialize = async () => {
      try {
        await initializeStorage();

        if (growthbook) {
          const checkReady = () =>
            new Promise<void>((resolve) => {
              if (growthbook.ready) {
                resolve();
              } else {
                const timeout = setTimeout(() => {
                  resolve();
                }, 2000);

                const checkInterval = setInterval(() => {
                  if (growthbook.ready) {
                    clearInterval(checkInterval);
                    clearTimeout(timeout);
                    resolve();
                  }
                }, 100);
              }
            });

          await checkReady();
        }

        setIsReady(true);
      } catch (error) {
        console.error("Error initializing AmberCopilot:", error);
        setIsReady(true);
      }
    };

    initialize();
  }, [growthbook]);

  const handleChatbotButtonClick = useCallback(() => {
    setShowButton(false);
    dispatch(setOpenAmberCopilot(true));
  }, [dispatch]);

  useEffect(() => {
    setShowButton(!isChatbotOpen);
  }, [isChatbotOpen]);

  useEffect(() => {
    const currentPathname = location.pathname;

    if (prevPathnameRef.current === null) {
      prevPathnameRef.current = currentPathname;
      return;
    }

    if (prevPathnameRef.current !== currentPathname && isChatbotOpen) {
      dispatch(setIsChatbotOpen(false));
      dispatch(setOpenAmberCopilot(false));
    }

    prevPathnameRef.current = currentPathname;
  }, [location.pathname, isChatbotOpen, dispatch]);

  useEffect(
    () => () => {
      if (isChatbotOpen) {
        dispatch(setIsChatbotOpen(false));
        dispatch(setOpenAmberCopilot(false));
      }
    },
    [isChatbotOpen, dispatch],
  );

  if (!isReady || !isAmberCopilotEnabled) {
    return null;
  }

  return (
    <>
      {showButton && !isSuccessPage && !isPaymentPage && !isExcludedPage && (
        <Suspense fallback={null}>
          <ChatbotButton onClick={handleChatbotButtonClick} />
        </Suspense>
      )}
      <Suspense fallback={null}>
        <Chatbot />
      </Suspense>
    </>
  );
};

export default wrapperHOC(AmberCopilotWrapper, {
  componentName: "AmberCopilotWrapper-Chatbot",
  showForChina: false,
  showForWhiteLable: false,
});
